<script setup>
import { computed, onMounted, provide, ref, useSlots } from "vue";

import BaseLayout from "@/Layouts/BaseLayout.vue";
import Footer from "@/Layouts/Footer.vue";
import AppMobileHeader from "@/Layouts/Partials/AppMobileHeader.vue";
import AppMobileNavbar from "@/Layouts/Partials/AppMobileNavbar.vue";
import LiveNotification from "@/Layouts/Partials/LiveNotification.vue";
import PrimarySidebar from "@/Layouts/Partials/PrimarySidebar.vue";
import SecondarySidebar from "@/Layouts/Partials/SecondarySidebar.vue";

import useSidebar from "@/composables/sidebar";
import { useResponsiveStore } from "@/stores/responsive";
import { usePage } from "@inertiajs/vue3";

const props = defineProps({
  title: {
    type: String,
    required: false
  },
  crumbs: {
    type: Array,
    required: false,
    default: () => []
  },
  bodyClass: {
    type: String,
    required: false
  },
  mainClass: {
    type: String,
    required: false
  },
  mainStyle: {
    type: Object,
    required: false
  },
  mainPanelClass: {
    type: String,
    required: false
  },
  appClass: {
    type: String,
    required: false
  },
  appStyle: {
    type: Object,
    required: false
  },
  screen: {
    type: Boolean,
    required: false
  },
  blank: {
    type: Boolean,
    required: false
  },
  hasHeader: {
    type: Boolean,
    required: false,
    default: true
  },
  hasNavbar: {
    type: Boolean,
    required: false,
    default: true
  },
  hasFooter: {
    type: Boolean,
    required: false,
    default: true
  }
});

const page = usePage();
const slot = useSlots();
const responsive = useResponsiveStore();
const sidebar = useSidebar();

const isLoaded = ref(false);
const hasSidebar = computed(() => !!slot.sidebar);
const user = computed(() => page.props.user);
const isLoggedIn = computed(() => !!user.value);

provide("AppLayoutProps", props);

onMounted(() => {
  sidebar.setSecondaryOpen(hasSidebar.value && isLoggedIn.value);
  isLoaded.value = true;

  try {
    if (window.location?.host?.includes("pintl.test") || window.location?.host?.includes("localhost")) {
      console.log(page.props);
    }
  } catch (e) {
    /*ignore*/
  }
});
</script>

<template>
  <BaseLayout
    :title="title"
    :body-class="[isLoggedIn ? 'has-primary' : '', bodyClass]"
    :main-class="mainClass"
    :app-class="appClass"
    :main-style="mainStyle"
    :app-style="appStyle"
    :screen="screen"
    :crumbs="crumbs"
  >
    <template #banner>
      <slot name="banner" />
    </template>
    <!--end::top-banner-->

    <template #sidebar>
      <PrimarySidebar v-if="isLoggedIn && responsive.mdAndUp" />
      <!--end::primary-sidebar-->

      <SecondarySidebar v-if="$slots.sidebar && responsive.mdAndUp && isLoggedIn">
        <slot name="sidebar" />
      </SecondarySidebar>
      <!--end::secondary-sidebar-->
    </template>

    <template v-if="hasHeader" #header>
      <slot v-if="$slots.header" name="header" />
      <AppMobileHeader v-if="!$slots.header && isLoggedIn" />
      <!--end::app-header-->
    </template>

    <template v-if="$slots.drawer" #drawer>
      <slot name="drawer" />
    </template>
    <!--end::right-sidebar-->

    <section class="main-section relative block w-full min-w-0 max-w-[100vw]">
      <div class="w-full min-w-0">
        <div class="main-content-wrapper w-full" :class="[blank ? '' : 'main-panel', mainPanelClass]">
          <slot :blank="blank" />
        </div>

        <Footer v-if="hasFooter" class="w-full" />
      </div>
    </section>

    <template v-if="hasNavbar" #navbar>
      <slot v-if="$slots.navbar" name="navbar" />
      <AppMobileNavbar v-if="!$slots.navbar && isLoggedIn" class="md:hidden">
        <template #default="slotProps">
          <slot name="subNavbar" v-bind="slotProps" />
        </template>
      </AppMobileNavbar>
      <!--end::mobile-nav-->
    </template>

    <!-- Helper -->
    <LiveNotification v-if="isLoggedIn" />
  </BaseLayout>
</template>
